import { EPriceTime, PricedisplayType, TimeOfUseTariffType } from '@/shared/types';

// 根据 index 和 displayType 获取对应的时间字符串 "HH:MM"
export const getItemTime = (index: number, displayType: PricedisplayType) => {
  const totalMinutes = displayType === PricedisplayType.HALF ? index * 30 * 60 : index * 60 * 60;
  const NowOrNext = totalMinutes >= 24 * 3600 ? '次日' : '';
  const hours = Math.floor(totalMinutes / 3600) % 24;
  const minutes = Math.floor((totalMinutes % 3600) / 60);
  if (
    (index === 48 && displayType === PricedisplayType.HOUR) ||
    (index === 96 && displayType === PricedisplayType.HALF)
  ) {
    return `后日00:00`;
  }
  return `${NowOrNext}${zeroPad(hours, 2)}:${zeroPad(minutes, 2)}`;
};

// 数字补零函数
export const zeroPad = (num: number, places: number) => String(num).padStart(places, '0');

// 根据时段数组和展示类型，生成每个时间点对应的类型数组
export const getItemTypes = (times: EPriceTime[], displayType: PricedisplayType) =>
  times.reduce(
    (acc, cur) => {
      acc.splice(
        cur.intervalStart,
        cur.intervalEnd - cur.intervalStart + 1,
        ...new Array(cur.intervalEnd - cur.intervalStart + 1).fill(cur.type)
      );
      return acc;
    },
    [...new Array(displayType === PricedisplayType.HALF ? 96 : 48)]
  );

// 合并连续同类型时段
export const getPeriodTypes = (times: EPriceTime[], displayType: PricedisplayType): EPriceTime[] =>
  getItemTypes(times, displayType).reduce((acc, cur, index) => {
    const last = acc[acc.length - 1];
    if (last && last.type === cur) {
      last.intervalEnd = index;
    } else {
      acc.push({
        type: cur,
        intervalStart: index,
        intervalEnd: index,
      });
    }
    return acc;
  }, [] as EPriceTime[]);

//从 intervalStart 和 intervalEnd解析出 "2024-06-01~2024-06-10"
export const getPeriodTime = (time: EPriceTime, displayType: PricedisplayType) => {
  return `${getItemTime(time.intervalStart, displayType)}-${getItemTime(time.intervalEnd + 1, displayType)}`;
};

//从 19:30~次日1:00 解析出 intervalStart 和 intervalEnd 和 displayType
export const parsePeriodTimes = (periods: string[], displayType: PricedisplayType): EPriceTime[] => {
  return periods.map(period => {
    // 支持 "次日08:00~09:00" 和 "08:00~次日09:00" 两种写法，可能出现后日
    const match = period.match(/^(次日|后日)?(\d{2}):(\d{2})-(次日|后日)?(\d{2}):(\d{2})$/);

    if (!match) {
      return {
        type: TimeOfUseTariffType.PLAIN,
        intervalStart: 0,
        intervalEnd: 0,
      };
    }

    const [, startNextDayA, startHourStr, startMinuteStr, endNextDayA, endHourStr, endMinuteStr] = match;

    // 判断是否"次日"或"后日"出现在开始或结束时间前
    const startDayOffset = startNextDayA === '后日' ? 2 : startNextDayA === '次日' ? 1 : 0;
    const endDayOffset = endNextDayA === '后日' ? 2 : endNextDayA === '次日' ? 1 : 0;

    const startHour = parseInt(startHourStr, 10);
    const startMinute = parseInt(startMinuteStr, 10);
    const endHour = parseInt(endHourStr, 10);
    const endMinute = parseInt(endMinuteStr, 10);

    // 计算开始索引
    const startIndex =
      displayType === PricedisplayType.HOUR
        ? startDayOffset * 24 + startHour
        : (startDayOffset * 24 + startHour) * 2 + (startMinute >= 30 ? 1 : 0);

    // 计算结束索引
    let endIndex;
    // 特殊处理：如果结束时间是次日00:00或后日00:00，表示到当天/次日的结束
    if (endHour === 0 && endMinute === 0 && endDayOffset > 0) {
      endIndex = displayType === PricedisplayType.HOUR ? endDayOffset * 24 - 1 : endDayOffset * 48 - 1;
    } else {
      endIndex =
        displayType === PricedisplayType.HOUR
          ? endDayOffset * 24 + endHour + (endMinute >= 30 ? 1 : 0) - 1
          : (endDayOffset * 24 + endHour) * 2 + (endMinute >= 30 ? 1 : 0) - 1;
    }

    return {
      type: TimeOfUseTariffType.PLAIN,
      intervalStart: startIndex,
      intervalEnd: endIndex,
    };
  });
};

// 判断时段是否覆盖全天
export const isFullDay = (times: EPriceTime[], displayType: PricedisplayType) =>
  !getItemTypes(times, displayType).some(t => t === undefined);

// 判断是否包含某种类型的时段
export const hasType = (times: EPriceTime[], type: TimeOfUseTariffType) => times && times.some(t => t.type === type);

/**
 * 将日期时间范围转换为特定格式的字符串
 * @param startDateTime - 开始时间，格式如 '2023-10-01T00:00:00'
 * @param endDateTime - 结束时间，格式如 '2023-10-01T01:00:00'
 * @param dateStr - 可选，参考日期，格式如 '20231001'，用于判断开始时间是否为次日
 * @return 格式化后的时间范围字符串，如 '00:00-次日01:00' 或 '00:00-后日01:00'
 */
export function formatDateTimeRange(startDateTime: string, endDateTime: string, dateStr: string): string {
  try {
    const start = new Date(startDateTime);
    const end = new Date(endDateTime);
    // 解析 dateStr 为基准日（保持在本地时区）
    if (dateStr.split('-').length === 3) {
      dateStr = dateStr.replace(/-/g, '');
    }
    let base: Date | null = null;
    if (dateStr && /^\d{8}$/.test(dateStr)) {
      const y = +dateStr.slice(0, 4);
      const m = +dateStr.slice(4, 6) - 1;
      const d = +dateStr.slice(6, 8);
      base = new Date(y, m, d, 0, 0, 0, 0);
    }
    const fmt = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

    const startStr = fmt(start);
    const endStr = fmt(end);

    // 判断"次日"或"后日"
    const startDayOffset = base ? Math.floor((start.getTime() - base.getTime()) / 86400000) : 0;
    const endDayOffset = base
      ? Math.floor((end.getTime() - base.getTime()) / 86400000)
      : Math.floor((end.getTime() - start.getTime()) / 86400000);

    const prefix = (dayOffset: number, t: string) => {
      if (dayOffset >= 2) return `后日${t}`;
      if (dayOffset >= 1) return `次日${t}`;
      return t;
    };

    return `${prefix(startDayOffset, startStr)}-${prefix(endDayOffset, endStr)}`;
  } catch (err) {
    return '无效时间格式';
  }
}

/**
 * 将格式化时间范围（如 '00:00-次日01:00' 或 '00:00-后日01:00'）解析回开始/结束 ISO 时间
 * @param formattedRange - 格式化时间段，如 '00:00-次日01:00' 或 '次日00:00-后日00:00'
 * @param baseDate - 基准日期 '20231001' 或 '2023-10-01'
 */
export function parseFormattedTimeRange(
  formattedRange: string,
  baseDate: string
): { startDateTime: string; endDateTime: string } {
  // 标准化 baseDate
  const baseStr = baseDate.includes('-')
    ? baseDate
    : `${baseDate.slice(0, 4)}-${baseDate.slice(4, 6)}-${baseDate.slice(6, 8)}`;

  const base = new Date(baseStr + 'T00:00:00');
  if (isNaN(base.getTime())) throw new Error('无效的 baseDate');

  // 规范化输入
  const norm = formattedRange.replace(/[～~–—]/g, '-').replace(/\s+/g, '');
  const [left, right] = norm.split('-');

  const startDayOffset = left.startsWith('后日') ? 2 : left.startsWith('次日') ? 1 : 0;
  const endDayOffset = right.startsWith('后日') ? 2 : right.startsWith('次日') ? 1 : 0;

  const parseTime = (s: string) => {
    const clean = s.replace(/^(次日|后日)/, '');
    const [h, m] = clean.split(':').map(Number);
    if ([h, m].some(isNaN)) throw new Error('无效时间');
    return { h, m };
  };

  const addDay = (date: Date, days: number) => new Date(date.getTime() + days * 86400000);

  const buildTime = (date: Date, h: number, m: number) => {
    const d = new Date(date);
    d.setHours(h, m, 0, 0);
    return d;
  };

  const start = buildTime(addDay(base, startDayOffset), parseTime(left).h, parseTime(left).m);
  const end = buildTime(addDay(base, endDayOffset), parseTime(right).h, parseTime(right).m);

  const fmtISO = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(
      d.getHours()
    ).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:00`;
  return { startDateTime: fmtISO(start), endDateTime: fmtISO(end) };
}

function toPrecision(str: string | undefined, precision: number) {
  if (!str) return;
  const [start, end] = str.split('.');
  return `${start}.${(end ?? '').padEnd(precision, '0')}`;
}
